import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect, type SelectOption } from 'components/ui';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { bankStatementService, coaLedgerService } from 'api/services';
import type {
  BankStatementDto, ParsedStatementPreview, StatementInspection, StatementColumnMap,
} from 'api/types';

// ============================================================================
// IMPORT STATEMENT WIZARD (ADR-0055 §3) — three steps:
//   1. File            — bank account + statement file (CSV / XLSX / XLS)
//   2. Map & preview   — the server reads the file's shape; a dropdown per field
//                        (pre-filled from the mapping remembered for these
//                        headers, else suggested from the header names), the
//                        date format (auto-detected), and a preview that
//                        refreshes as the mapping changes
//   3. Import          — reference + balances → POST / (hash-deduped); the
//                        mapping is remembered for the next file with the same
//                        columns. The statement is then worked on whenever
//                        convenient — nothing has to be finished in one go.
// Withdrawal = money out, Deposit = money in (stored as the bank's Dr/Cr).
// ============================================================================

const money = (n: number) => (n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (s?: string | null) => {
  if (!s) return '—';
  const d = new Date(s.length === 10 ? `${s}T00:00:00` : s);
  return isNaN(d.getTime()) ? s : d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};
const num = (s: string) => { const n = parseFloat(s); return isNaN(n) ? 0 : n; };

const labelStyle: React.CSSProperties = { fontSize: '0.8125rem', marginBottom: '0.25rem', display: 'block', color: 'var(--text-secondary)', fontWeight: 500 };
const inputStyle: React.CSSProperties = { fontSize: '0.8125rem', padding: '0.375rem 0.625rem' };
const noteStyle: React.CSSProperties = { fontSize: '0.78rem', borderRadius: '0.5rem', padding: '0.5rem 0.75rem', marginBottom: '0.75rem' };

type AmountLayout = 'PAIR' | 'SINGLE';

type ColumnField = 'dateColumn' | 'valueDateColumn' | 'descriptionColumn' | 'referenceColumn'
  | 'withdrawalColumn' | 'depositColumn' | 'amountColumn' | 'drcrColumn' | 'balanceColumn';

export interface ImportStatementWizardProps {
  isOpen: boolean;
  /** Called with the committed statement — the screen opens it. */
  onImported: (statement: BankStatementDto) => void;
  onClose: () => void;
}

export const ImportStatementWizard: React.FC<ImportStatementWizardProps> = ({ isOpen, onImported, onClose }) => {
  const [step, setStep] = useState<1 | 2 | 3>(1);

  // Step 1
  const [bankLedgerId, setBankLedgerId] = useState('');
  const [bankLedgerLabel, setBankLedgerLabel] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const ledgerLabelCache = useRef<Map<string, string>>(new Map());

  // Step 2
  const [inspection, setInspection] = useState<StatementInspection | null>(null);
  const [mapping, setMapping] = useState<StatementColumnMap | null>(null);
  const [dateConfirmed, setDateConfirmed] = useState(true);
  const [preview, setPreview] = useState<ParsedStatementPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const previewSeq = useRef(0);

  // Step 3
  const [statementRef, setStatementRef] = useState('');
  const [openingBalance, setOpeningBalance] = useState('');
  const [closingBalance, setClosingBalance] = useState('');
  const balancesTouched = useRef(false);

  const [inspecting, setInspecting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setBankLedgerId(''); setBankLedgerLabel(''); setFile(null);
    setInspection(null); setMapping(null); setDateConfirmed(true); setPreview(null); setPreviewing(false);
    setStatementRef(''); setOpeningBalance(''); setClosingBalance(''); balancesTouched.current = false;
    setInspecting(false); setImporting(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [isOpen]);

  const loadBankLedgers = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await coaLedgerService.getAll({ pageSize: 50, searchTerm: input || undefined, ledgerNature: 'BANK' });
      res.data.forEach(l => ledgerLabelCache.current.set(l.id, `${l.ledgerCode} - ${l.ledgerName}`));
      return res.data.map(l => ({ value: l.id, label: `${l.ledgerCode} - ${l.ledgerName}` }));
    } catch {
      return [];
    }
  }, []);

  // ---- Step 1 → 2: read the file's shape ----

  const applyInspection = useCallback((ins: StatementInspection) => {
    setInspection(ins);
    const m = { ...ins.mapping };
    if (!m.dateFormat) m.dateFormat = ins.dateDetection.chosen ?? null;
    setMapping(m);
    // Ambiguous dates must be confirmed by the user — unless a remembered mapping already decided.
    setDateConfirmed(!ins.dateDetection.ambiguous || (!!ins.savedMapping && !!ins.mapping.dateFormat));
  }, []);

  const inspect = useCallback(async (headerRow?: number) => {
    if (!file || !bankLedgerId) return;
    setInspecting(true);
    try {
      const { inspection: ins } = await bankStatementService.inspect(file, bankLedgerId, headerRow);
      applyInspection(ins);
      setStep(2);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not read the statement file.');
    } finally {
      setInspecting(false);
    }
  }, [file, bankLedgerId, applyInspection]);

  // ---- Live preview: re-map on every mapping change (debounced) ----

  useEffect(() => {
    if (step !== 2 || !file || !mapping) return;
    const seq = ++previewSeq.current;
    const t = setTimeout(async () => {
      setPreviewing(true);
      try {
        const { preview: p } = await bankStatementService.parse(file, mapping);
        if (seq !== previewSeq.current) return;
        setPreview(p);
        if (!balancesTouched.current) {
          setOpeningBalance(p.impliedOpeningBalance != null ? String(p.impliedOpeningBalance) : '');
          setClosingBalance(p.impliedClosingBalance != null ? String(p.impliedClosingBalance) : '');
        }
      } catch (err: any) {
        if (seq === previewSeq.current) notificationService.toast.error(err?.response?.data?.message || 'Could not read the statement with this mapping.');
      } finally {
        if (seq === previewSeq.current) setPreviewing(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [step, file, mapping]);

  // ---- Mapping editors ----

  const columnOptions: SelectOption[] = useMemo(() => {
    if (!inspection) return [];
    return [
      { value: '', label: '— not in this file —' },
      ...inspection.columns.map(c => ({
        value: String(c.index),
        label: `${c.letter} · ${c.header || '(no header)'}${c.samples[0] ? `  — e.g. ${c.samples[0].slice(0, 40)}` : ''}`,
      })),
    ];
  }, [inspection]);

  const headerRowOptions: SelectOption[] = useMemo(() => {
    if (!inspection) return [];
    return [
      { value: '0', label: 'No header row (data starts on row 1)' },
      ...inspection.topRows.map((r, i) => ({
        value: String(i + 1),
        label: `Row ${i + 1}: ${r.filter(c => c && c.trim()).slice(0, 6).join(' | ').slice(0, 100) || '(blank)'}`,
      })),
    ];
  }, [inspection]);

  const dateOptions: SelectOption[] = useMemo(() => {
    if (!inspection) return [];
    const fitting = new Set(inspection.dateDetection.fitting);
    const all = inspection.dateFormats.map(f => ({
      value: f.pattern,
      label: `${f.label}${fitting.has(f.pattern) ? '   ✓ fits every date' : ''}`,
    }));
    return [...all.filter(o => fitting.has(o.value)), ...all.filter(o => !fitting.has(o.value))];
  }, [inspection]);

  const layout: AmountLayout = mapping?.amountColumn ? 'SINGLE' : 'PAIR';

  const setColumn = useCallback((field: ColumnField, v: string) => {
    setMapping(prev => {
      if (!prev) return prev;
      const col = v ? parseInt(v, 10) : null;
      const next: StatementColumnMap = { ...prev };
      // A column feeds one field: taking it here releases it elsewhere.
      if (col != null) {
        (['dateColumn', 'valueDateColumn', 'descriptionColumn', 'referenceColumn', 'withdrawalColumn', 'depositColumn', 'amountColumn', 'drcrColumn', 'balanceColumn'] as ColumnField[])
          .forEach(f => { if (f !== field && next[f] === col) next[f] = null; });
      }
      next[field] = col;
      // A new date column has its own layout — let the server detect it again.
      if (field === 'dateColumn') next.dateFormat = null;
      return next;
    });
    if (field === 'dateColumn') setDateConfirmed(true);
  }, []);

  const setLayout = useCallback((l: AmountLayout) => {
    setMapping(prev => {
      if (!prev) return prev;
      if (l === 'PAIR') return { ...prev, amountColumn: null, drcrColumn: null, drcrCreditValue: null };
      return { ...prev, withdrawalColumn: null, depositColumn: null };
    });
  }, []);

  const colValue = (field: ColumnField) => (mapping?.[field] ? String(mapping[field]) : '');

  const fieldSelect = (field: ColumnField, label: React.ReactNode) => (
    <div>
      <SyutSelect label={label} options={columnOptions} value={colValue(field)} menuPortalTarget={document.body}
        onChange={(v) => setColumn(field, (v as string) || '')} isClearable={false} size="sm" />
    </div>
  );

  // ---- Commit ----

  const totals = useMemo(() => {
    if (!preview) return { cr: 0, dr: 0 };
    return preview.lines.reduce((acc, l) => ({ cr: acc.cr + (l.crAmount || 0), dr: acc.dr + (l.drAmount || 0) }), { cr: 0, dr: 0 });
  }, [preview]);

  const balanceCheck = useMemo(() => {
    if (openingBalance === '' || closingBalance === '' || !preview) return null;
    const expected = num(openingBalance) + totals.cr - totals.dr;
    return { expected, diff: Math.round((num(closingBalance) - expected) * 100) / 100 };
  }, [openingBalance, closingBalance, preview, totals]);

  const handleImport = useCallback(async () => {
    if (!preview || preview.lines.length === 0 || !mapping || !inspection) return;
    setImporting(true);
    try {
      const { statement, message } = await bankStatementService.import({
        bankLedgerId,
        statementRef: statementRef.trim() || undefined,
        openingBalance: openingBalance !== '' ? num(openingBalance) : undefined,
        closingBalance: closingBalance !== '' ? num(closingBalance) : undefined,
        fileName: file?.name || undefined,
        lines: preview.lines,
        mapping: { ...mapping, dateFormat: mapping.dateFormat || preview.dateFormatUsed || null },
        headerFingerprint: inspection.headerFingerprint || undefined,
        headerLabels: inspection.headerLabels,
        fileType: inspection.fileType,
      });
      notificationService.toast.success(message || `Statement imported: ${statement.lineCount} lines.`);
      onImported(statement);
      onClose();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not import the statement.');
    } finally {
      setImporting(false);
    }
  }, [preview, mapping, inspection, bankLedgerId, statementRef, openingBalance, closingBalance, file, onImported, onClose]);

  const isDirty = !!file || !!bankLedgerId || step > 1;
  const handleClose = useCallback(() => { if (isDirty) setDiscardConfirmOpen(true); else onClose(); }, [isDirty, onClose]);

  if (!isOpen) return null;
  const busy = inspecting || importing;
  const canContinue = !!preview && preview.lines.length > 0 && dateConfirmed && !previewing;
  const dd = inspection?.dateDetection;
  const dateLabel = inspection?.dateFormats.find(f => f.pattern === mapping?.dateFormat)?.label.split('  ')[0];

  const stepBadge = (n: 1 | 2 | 3, label: string) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.8125rem', color: step === n ? 'var(--text-primary)' : 'var(--text-muted)', fontWeight: step === n ? 700 : 400 }}>
      <span style={{
        width: 20, height: 20, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '0.72rem', fontWeight: 700,
        background: step >= n ? '#2563eb' : 'var(--bg-secondary)', color: step >= n ? '#fff' : 'var(--text-muted)',
        border: step >= n ? 'none' : '1px solid var(--border-color)',
      }}>{n}</span>
      {label}
    </span>
  );

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-xl">
        {busy && (
          <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit', gap: '0.75rem' }}>
            <div style={{ width: '2rem', height: '2rem', border: '3px solid var(--border-color)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              {importing ? 'Importing…' : 'Reading the file…'}
            </span>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Import Bank Statement</h2>
          <button className="modal-close" onClick={handleClose} disabled={busy}><IconSystem.x size={20} /></button>
        </div>

        <div className="modal-content">
          <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', marginBottom: '1rem' }}>
            {stepBadge(1, 'File')}
            <span style={{ color: 'var(--text-muted)' }}>›</span>
            {stepBadge(2, 'Map & preview')}
            <span style={{ color: 'var(--text-muted)' }}>›</span>
            {stepBadge(3, 'Import')}
          </div>

          {/* ---- STEP 1: FILE ---- */}
          {step === 1 && (
            <>
              <div className="form-grid-2" style={{ alignItems: 'flex-end' }}>
                <div>
                  <SyutSelect label={<>Bank account <span className="text-danger">*</span></>} isAsync loadOptions={loadBankLedgers}
                    defaultOptions cacheOptions={false} menuPortalTarget={document.body}
                    seedOptions={bankLedgerId ? [{ value: bankLedgerId, label: bankLedgerLabel }] : undefined}
                    value={bankLedgerId}
                    onChange={(v) => { const id = (v as string) || ''; setBankLedgerId(id); setBankLedgerLabel(id ? (ledgerLabelCache.current.get(id) || '') : ''); }}
                    placeholder="Pick the bank ledger" isClearable size="sm" />
                </div>
                <div>
                  <label style={labelStyle}>Statement file <span className="text-danger">*</span></label>
                  <input ref={fileInputRef} type="file" accept=".csv,.txt,.xlsx,.xls" className="form-input"
                    onChange={(e) => setFile(e.target.files?.[0] || null)} style={{ ...inputStyle, padding: '0.3rem' }} />
                </div>
              </div>
              <div style={{ ...noteStyle, marginTop: '0.75rem', background: 'var(--bg-secondary)', color: 'var(--text-secondary)' }}>
                Upload the file exactly as the bank gives it — CSV, Excel (.xlsx) or old Excel (.xls). FairBooks finds the
                header row and suggests the columns; you check them on the next step. A mapping you confirm is remembered,
                so the next statement with the same columns needs no setup.
              </div>
            </>
          )}

          {/* ---- STEP 2: MAP & PREVIEW ---- */}
          {step === 2 && inspection && mapping && (
            <>
              {inspection.savedMapping ? (
                <div style={{ ...noteStyle, background: 'rgba(22,163,74,0.08)', border: '1px solid rgba(22,163,74,0.35)', color: 'var(--text-primary)' }}>
                  <IconSystem.check size={13} /> Using the mapping saved from an earlier statement with these columns
                  (<strong>{inspection.savedMapping.formatName}</strong>{inspection.savedMapping.sameBankAccount ? '' : ', saved on another bank account'}).
                  Change anything below if this file differs.
                </div>
              ) : (
                <div style={{ ...noteStyle, background: 'rgba(37,99,235,0.07)', border: '1px solid rgba(37,99,235,0.3)', color: 'var(--text-primary)' }}>
                  Columns suggested from the file's headers — please check them against the preview. Once imported, this
                  mapping is remembered for the next file with the same columns.
                </div>
              )}

              <div className="form-grid-3">
                <div>
                  <SyutSelect label="Header row" options={headerRowOptions} value={String(inspection.headerRow)} menuPortalTarget={document.body}
                    onChange={(v) => inspect(parseInt((v as string) || '0', 10))} isClearable={false} size="sm" />
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <SyutSelect label={<>Date format <span className="text-danger">*</span></>} options={dateOptions}
                    value={mapping.dateFormat || ''} menuPortalTarget={document.body}
                    onChange={(v) => { setMapping(prev => prev ? { ...prev, dateFormat: (v as string) || null } : prev); setDateConfirmed(true); }}
                    placeholder={dd && dd.fitting.length === 0 ? 'No layout fits every date — pick the closest' : 'Pick the date format'}
                    isClearable={false} size="sm" />
                </div>
              </div>

              {dd?.ambiguous && !dateConfirmed && (
                <div style={{ ...noteStyle, marginTop: '0.5rem', background: 'rgba(217,119,6,0.09)', border: '1px solid rgba(217,119,6,0.4)', color: 'var(--text-primary)' }}>
                  Every date in this file has a day of 12 or less, so it could be <strong>day-first</strong> or <strong>month-first</strong>.
                  Confirm the bank's layout: {' '}
                  <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '0.15rem 0.5rem' }}
                    onClick={() => setDateConfirmed(true)}>
                    Yes, {dateLabel || 'as selected'}
                  </button>{' '}
                  or pick another format above.
                </div>
              )}
              {dd && dd.fitting.length === 0 && dd.valuesChecked > 0 && (
                <div style={{ ...noteStyle, marginTop: '0.5rem', background: 'rgba(220,38,38,0.07)', border: '1px solid rgba(220,38,38,0.4)', color: 'var(--text-primary)' }}>
                  No supported date layout fits every value in the date column ({dd.bestFitCount} of {dd.valuesChecked} fit the closest).
                  Check that the right column is chosen as the date.
                </div>
              )}

              <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', margin: '0.75rem 0 0.35rem' }}>Columns</div>
              <div className="form-grid-4">
                {fieldSelect('dateColumn', <>Transaction date <span className="text-danger">*</span></>)}
                {fieldSelect('valueDateColumn', 'Value date')}
                {fieldSelect('descriptionColumn', 'Description / narration')}
                {fieldSelect('referenceColumn', 'Reference / cheque / UTR')}
              </div>

              <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'center', fontSize: '0.8125rem', margin: '0.6rem 0 0.35rem' }}>
                <span style={{ color: 'var(--text-secondary)', fontWeight: 600, fontSize: '0.78rem' }}>Amounts are in</span>
                <label style={{ display: 'inline-flex', gap: '0.35rem', alignItems: 'center', cursor: 'pointer' }}>
                  <input type="radio" checked={layout === 'PAIR'} onChange={() => setLayout('PAIR')} /> separate Withdrawal and Deposit columns
                </label>
                <label style={{ display: 'inline-flex', gap: '0.35rem', alignItems: 'center', cursor: 'pointer' }}>
                  <input type="radio" checked={layout === 'SINGLE'} onChange={() => setLayout('SINGLE')} /> one Amount column
                </label>
              </div>
              <div className="form-grid-4">
                {layout === 'PAIR' ? (
                  <>
                    {fieldSelect('withdrawalColumn', <>Withdrawal (money out) <span className="text-danger">*</span></>)}
                    {fieldSelect('depositColumn', <>Deposit (money in) <span className="text-danger">*</span></>)}
                  </>
                ) : (
                  <>
                    {fieldSelect('amountColumn', <>Amount <span className="text-danger">*</span></>)}
                    {fieldSelect('drcrColumn', 'Dr/Cr marker column')}
                    <div>
                      <label style={labelStyle}>Marker meaning deposit</label>
                      <input type="text" className="form-input" value={mapping.drcrCreditValue || ''} disabled={!mapping.drcrColumn}
                        onChange={(e) => setMapping(prev => prev ? { ...prev, drcrCreditValue: e.target.value || null } : prev)}
                        placeholder={mapping.drcrColumn ? 'e.g. CR' : 'no marker: + is deposit'} style={inputStyle} />
                    </div>
                  </>
                )}
                {fieldSelect('balanceColumn', 'Running balance')}
              </div>

              {/* Live preview */}
              <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', alignItems: 'center', fontSize: '0.8125rem', background: 'var(--bg-secondary)', borderRadius: '0.5rem', padding: '0.5rem 0.75rem', margin: '0.85rem 0 0.6rem' }}>
                {previewing && <span style={{ color: 'var(--text-muted)' }}>Updating preview…</span>}
                {preview && (
                  <>
                    <span><span style={{ color: 'var(--text-muted)' }}>Lines </span><strong>{preview.lines.length}</strong></span>
                    <span><span style={{ color: 'var(--text-muted)' }}>Need attention </span><strong style={{ color: preview.errors.length ? '#dc2626' : 'inherit' }}>{preview.errors.length}</strong></span>
                    <span><span style={{ color: 'var(--text-muted)' }}>Skipped (blank / totals) </span><strong>{preview.skippedRows}</strong></span>
                    <span><span style={{ color: 'var(--text-muted)' }}>Withdrawals </span><strong>{money(totals.dr)}</strong></span>
                    <span><span style={{ color: 'var(--text-muted)' }}>Deposits </span><strong>{money(totals.cr)}</strong></span>
                  </>
                )}
              </div>

              {preview && preview.errors.length > 0 && (
                <div style={{ border: '1px solid #dc2626', borderRadius: '0.5rem', padding: '0.5rem 0.75rem', marginBottom: '0.6rem', maxHeight: 110, overflow: 'auto' }}>
                  <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#dc2626', marginBottom: '0.25rem' }}>Rows that could not be read — adjust the mapping, or they are left out:</div>
                  {preview.errors.map((e, i) => (
                    <div key={i} style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{e}</div>
                  ))}
                </div>
              )}

              <div style={{ maxHeight: '36vh', overflow: 'auto', border: '1px solid var(--border-color)', borderRadius: '0.5rem' }}>
                <table className="syut-grid" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                  <thead>
                    <tr style={{ background: 'var(--grid-header-bg, #dbeafe)', position: 'sticky', top: 0 }}>
                      <th style={{ width: 50, textAlign: 'center', padding: '0.45rem 0.5rem' }}>Row</th>
                      <th style={{ width: 150, textAlign: 'left', padding: '0.45rem 0.5rem' }}>Date</th>
                      <th style={{ textAlign: 'left', padding: '0.45rem 0.5rem' }}>Description</th>
                      <th style={{ width: 140, textAlign: 'left', padding: '0.45rem 0.5rem' }}>Reference</th>
                      <th style={{ width: 110, textAlign: 'right', padding: '0.45rem 0.5rem' }}>Withdrawal</th>
                      <th style={{ width: 110, textAlign: 'right', padding: '0.45rem 0.5rem' }}>Deposit</th>
                      <th style={{ width: 120, textAlign: 'right', padding: '0.45rem 0.5rem' }}>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview && preview.lines.length === 0 && (
                      <tr><td colSpan={7} style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-muted)' }}>Nothing read yet — check the header row and the date and amount columns.</td></tr>
                    )}
                    {preview?.lines.map(l => (
                      <tr key={l.lineNo} style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{l.sourceRow ?? l.lineNo}</td>
                        <td style={{ padding: '0.3rem 0.5rem' }}>
                          <strong>{fmtDate(l.txnDate)}</strong>
                          {l.rawTxnDate && <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}> ← {l.rawTxnDate}</span>}
                        </td>
                        <td style={{ padding: '0.3rem 0.5rem' }}>{l.description || '—'}</td>
                        <td style={{ padding: '0.3rem 0.5rem' }}>{l.reference || '—'}</td>
                        <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right' }}>{l.drAmount ? money(l.drAmount) : ''}</td>
                        <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right' }}>{l.crAmount ? money(l.crAmount) : ''}</td>
                        <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right', color: 'var(--text-muted)' }}>{l.runningBalance != null ? money(l.runningBalance) : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* ---- STEP 3: IMPORT ---- */}
          {step === 3 && preview && (
            <>
              <div className="form-grid-3" style={{ alignItems: 'flex-end' }}>
                <div>
                  <label style={labelStyle}>Statement reference</label>
                  <input type="text" className="form-input" value={statementRef}
                    onChange={(e) => setStatementRef(e.target.value)} placeholder="e.g. OCT-2026" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Opening balance</label>
                  <input type="number" inputMode="decimal" className="form-input" value={openingBalance}
                    onChange={(e) => { balancesTouched.current = true; setOpeningBalance(e.target.value); }} placeholder="optional" style={{ ...inputStyle, textAlign: 'right' }} />
                </div>
                <div>
                  <label style={labelStyle}>Closing balance</label>
                  <input type="number" inputMode="decimal" className="form-input" value={closingBalance}
                    onChange={(e) => { balancesTouched.current = true; setClosingBalance(e.target.value); }} placeholder="optional" style={{ ...inputStyle, textAlign: 'right' }} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem 1rem', fontSize: '0.8125rem', marginTop: '1rem' }}>
                <div><div style={{ color: 'var(--text-muted)' }}>Bank account</div><strong>{bankLedgerLabel || '—'}</strong></div>
                <div><div style={{ color: 'var(--text-muted)' }}>File</div><strong>{file?.name || '—'}</strong></div>
                <div><div style={{ color: 'var(--text-muted)' }}>Lines to import</div><strong>{preview.lines.length}</strong></div>
                <div><div style={{ color: 'var(--text-muted)' }}>Withdrawals</div><strong>{money(totals.dr)}</strong></div>
                <div><div style={{ color: 'var(--text-muted)' }}>Deposits</div><strong>{money(totals.cr)}</strong></div>
                <div>
                  <div style={{ color: 'var(--text-muted)' }}>Balance check</div>
                  {balanceCheck == null ? <strong>—</strong>
                    : balanceCheck.diff === 0
                      ? <strong style={{ color: '#16a34a' }}>Opening + deposits − withdrawals = closing</strong>
                      : <strong style={{ color: '#d97706' }}>Differs by {money(balanceCheck.diff)} (expected closing {money(balanceCheck.expected)})</strong>}
                </div>
                <div style={{ gridColumn: '1 / -1', color: 'var(--text-secondary)' }}>
                  Lines already imported from an earlier statement are skipped automatically. The column mapping is
                  remembered for the next file with the same columns. After the import you can reconcile the statement
                  now or come back to it any time.
                </div>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={busy}>Cancel</button>
          {step > 1 && (
            <button className="btn btn-secondary" onClick={() => setStep(step === 3 ? 2 : 1)} disabled={busy}>Back</button>
          )}
          {step === 1 && (
            <button className="btn btn-primary" onClick={() => inspect()} disabled={busy || !bankLedgerId || !file}>
              Read file
            </button>
          )}
          {step === 2 && (
            <button className="btn btn-primary" onClick={() => setStep(3)} disabled={busy || !canContinue}
              title={!dateConfirmed ? 'Confirm the date format first' : undefined}>
              Continue
            </button>
          )}
          {step === 3 && (
            <button className="btn btn-primary" onClick={handleImport} disabled={busy || !preview || preview.lines.length === 0}>
              <IconSystem.check size={16} /> Import statement
            </button>
          )}
        </div>
      </div>

      <ConfirmDialog open={discardConfirmOpen} title="Discard Import?"
        message="The statement has not been imported yet. Discard this import?"
        confirmLabel="Discard" cancelLabel="Keep" variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }} onCancel={() => setDiscardConfirmOpen(false)} />
    </>
  );
};

export default ImportStatementWizard;
